import { useState } from "react";
import { useAuth } from "../lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Mail, Lock, User, AlertCircle, Loader2, CheckCircle, FileCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_HINT = "Gmail, Yahoo, or Outlook email required";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const result = await signup(name, email, password);
      if (result.trial_skipped) {
        // Account created but no free trial — show warning and redirect
        toast.warning(result.trial_message, { duration: 8000 });
        navigate("/app/pricing");
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page" data-testid="signup-page">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <div className="lp-logo-icon"><FileCheck size={16} /></div>
            <span className="auth-logo">ResumeAI</span>
          </Link>
          <h1>Start your free trial</h1>
          <p>50 scans free for 7 days. No credit card required.</p>
        </div>

        {error && (
          <div className="auth-error" data-testid="signup-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="name">Full Name</label>
            <div className="auth-input-wrap">
              <User size={16} className="auth-input-icon" />
              <input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="signup-name"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <div className="auth-input-wrap">
              <Mail size={16} className="auth-input-icon" />
              <input
                id="email"
                type="email"
                placeholder="you@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="signup-email"
              />
            </div>
            <span className="auth-hint">{ALLOWED_HINT}</span>
          </div>

          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <div className="auth-input-wrap">
              <Lock size={16} className="auth-input-icon" />
              <input
                id="password"
                type="password"
                placeholder="Min. 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="signup-password"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="confirm">Confirm Password</label>
            <div className="auth-input-wrap">
              {password && confirm && password === confirm ? (
                <CheckCircle size={16} className="auth-input-icon" style={{ color: "#10b981" }} />
              ) : (
                <Lock size={16} className="auth-input-icon" />
              )}
              <input
                id="confirm"
                type="password"
                placeholder="Repeat password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="signup-confirm"
              />
            </div>
          </div>

          <button type="submit" className="auth-btn" disabled={loading} data-testid="signup-submit">
            {loading ? <Loader2 size={18} className="spin" /> : <UserPlus size={18} />}
            {loading ? "Creating account..." : "Start Free Trial"}
          </button>
        </form>

        <div className="auth-trial-info">
          <CheckCircle size={14} /> 50 free scans
          <span className="auth-dot" />
          7-day trial
          <span className="auth-dot" />
          No credit card
        </div>

        <div className="auth-footer">
          Already have an account?{" "}
          <Link to="/login" data-testid="goto-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
